import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { tasksApi } from '../services/api';
import { GenerationTask, GenerationType } from '../types/generation';

// Кэш для задач
const TASKS_CACHE_KEY_PREFIX = 'tasks_cache_v2';
const TASKS_QUERY_KEY = ['tasks'];

// Размер страницы для пагинации
const PAGE_SIZE = 20;

// Интерфейс для данных страницы
interface TasksPageData {
  tasks: GenerationTask[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

// Функции для работы с кэшем
const getTasksCacheKey = (token: string | null): string => {
  if (!token) return `${TASKS_CACHE_KEY_PREFIX}:guest`;

  // Пытаемся извлечь userId из JWT, чтобы кэш был user-scoped и не смешивал задачи между пользователями.
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return `${TASKS_CACHE_KEY_PREFIX}:unknown`;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const decoded: any = JSON.parse(jsonPayload);

    const userId =
      decoded?.sub ||
      decoded?.nameid ||
      decoded?.userId ||
      decoded?.id ||
      decoded?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'];

    if (userId) return `${TASKS_CACHE_KEY_PREFIX}:${String(userId)}`;
  } catch {
    // ignore
  }

  // Фолбэк: привязываем кэшь к последним символам токена (всё равно лучше, чем общий ключ)
  return `${TASKS_CACHE_KEY_PREFIX}:t_${token.slice(-12)}`;
};

const readTasksCache = (token: string | null): GenerationTask[] | null => {
  try {
    const cached = localStorage.getItem(getTasksCacheKey(token));
    return cached ? JSON.parse(cached) : null;
  } catch (error) {
    console.error('Ошибка чтения кэша задач:', error);
    return null;
  }
};

// Функция для записи задач в localStorage (только легкие данные)
const writeTasksCache = (token: string | null, tasks: GenerationTask[]) => {
  try {
    const tasksToCache = tasks.slice(0, 20);
    const lightTasks = tasksToCache.map(task => ({
      id: task.id,
      type: task.type,
      status: task.status,
      title: task.title,
      name: task.name,
      isViewed: task.isViewed,
      error: task.error,
      knowledgeDocumentId: task.knowledgeDocumentId,
      startedAt: task.startedAt,
      completedAt: task.completedAt,
      progress: task.progress
    }));
    localStorage.setItem(getTasksCacheKey(token), JSON.stringify(lightTasks));
  } catch (error) {
    console.error('Ошибка записи кэша задач:', error);
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      console.warn('Кэш задач очищен из-за превышения квоты localStorage');
      try {
        localStorage.removeItem(getTasksCacheKey(token));
        const limitedTasks = tasks.slice(0, 10).map(task => ({
          id: task.id,
          type: task.type,
          status: task.status,
          title: task.title,
          name: task.name,
          isViewed: task.isViewed,
          error: task.error,
          knowledgeDocumentId: task.knowledgeDocumentId,
          startedAt: task.startedAt,
          completedAt: task.completedAt,
          progress: task.progress
        }));
        localStorage.setItem(getTasksCacheKey(token), JSON.stringify(limitedTasks));
      } catch (cleanupError) {
        console.error('Ошибка очистки кэша:', cleanupError);
      }
    }
  }
};

// Дебаунс обновлений кэша
let updateCacheTimeout: NodeJS.Timeout | null = null;
const pendingUpdates = new Map<string, GenerationTask>();
const CACHE_UPDATE_DEBOUNCE = 100;

// Безопасная обработка дат: никогда не возвращаем 0, сохраняем лучшее известное значение
const safeParseDate = (dateString: string | null | undefined, previous?: number): number => {
  if (!dateString) return previous ?? Date.now();
  try {
    const d = new Date(dateString);
    return isNaN(d.getTime()) ? (previous ?? Date.now()) : d.getTime();
  } catch {
    return previous ?? Date.now();
  }
};

// Обновление задач в кэше (мерж полей)
export const updateTasksCache = (queryClient: any, updatedTasks: GenerationTask[], immediate = false) => {
  if (!updatedTasks?.length) return;

  const applyUpdate = (tasksToUpdate: GenerationTask[]) => {
    queryClient.setQueryData(TASKS_QUERY_KEY, (oldData: any) => {
      if (!oldData || !Array.isArray(oldData.pages)) return oldData;

      const updatesById = new Map(tasksToUpdate.map(t => [t.id, t]));
      const newPages = oldData.pages.map((page: TasksPageData, pageIndex: number) => {
        if (!page?.tasks) return page;
        const merged = page.tasks.map((existing: GenerationTask) => {
          const upd = updatesById.get(existing.id);
          if (!upd) return existing;
          // Мержим поля, НЕ затираем корректные даты и заголовки пустыми значениями.
          // Если статус меняется на pending (перезапуск), то startedAt ДОЛЖЕН обновиться.
          const isRetry = upd.status === 'pending' && existing.status !== 'pending';
          
          return {
            ...existing,
            ...upd,
            startedAt: isRetry ? (upd.startedAt || Date.now()) : (upd.startedAt ? upd.startedAt : existing.startedAt),
            completedAt: upd.completedAt ? upd.completedAt : (isRetry ? undefined : existing.completedAt),
            title: upd.title || existing.title,
            name: upd.name || existing.name,
            progress: typeof upd.progress === 'number' ? upd.progress : existing.progress
          } as GenerationTask;
        });

        // Добавление новых задач - только на первую страницу и в начало
        if (pageIndex === 0) {
          const existingIds = new Set(merged.map(t => t.id));
          const newOnes = tasksToUpdate.filter(t => !existingIds.has(t.id));
          return { ...page, tasks: newOnes.length ? [...newOnes, ...merged] : merged };
        }

        return { ...page, tasks: merged };
      });
      return { ...oldData, pages: newPages };
    });
  };

  if (immediate) {
    applyUpdate(updatedTasks);
    return;
  }
  updatedTasks.forEach(t => pendingUpdates.set(t.id, t));
  if (updateCacheTimeout) clearTimeout(updateCacheTimeout);
  updateCacheTimeout = setTimeout(() => {
    const batch = Array.from(pendingUpdates.values());
    pendingUpdates.clear();
    if (batch.length) applyUpdate(batch);
  }, CACHE_UPDATE_DEBOUNCE);
};

// Хук для получения задач с пагинацией и автоподгрузкой
export const useTasks = () => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
  const cachedTasks = readTasksCache(token);

  return useInfiniteQuery({
    queryKey: TASKS_QUERY_KEY,
    queryFn: async ({ pageParam = 1 }): Promise<TasksPageData> => {
      try {
        const response = await tasksApi.getTasks(pageParam, PAGE_SIZE);
        const mappedTasks = response.data.tasks.map((apiTask: any): GenerationTask => {
          let status: 'pending' | 'completed' | 'error' | 'cancelled' | 'limit-exceeded' = 'pending';
          const statusString = String(apiTask.status || '').toLowerCase();
          if (statusString === 'successful' || statusString === 'successfull') status = 'completed';
          else if (statusString === 'cancelled') status = 'cancelled';
          else if (statusString === 'error' || statusString === 'failed') status = 'error';
          else if (statusString === 'inprogress') status = 'pending';
          else if (statusString === 'limitexceeded') status = 'limit-exceeded';

          const mapped: GenerationTask = {
            id: apiTask.id,
            type: (apiTask.type || 'unknown').toLowerCase() as GenerationType,
            title: apiTask.name || `${apiTask.type || 'Unknown'} Generation`,
            status,
            progress: status === 'completed' ? 100 : (apiTask.progress || 0),
            startedAt: safeParseDate(apiTask.startTime),
            completedAt: apiTask.endTime ? safeParseDate(apiTask.endTime) : undefined,
            canCancel: status === 'pending',
            data: undefined,
            redirected: false,
            name: apiTask.name,
            isViewed: apiTask.isViewed,
            error: apiTask.errorMessage,
            knowledgeDocumentId: apiTask.knowledgeDocumentId
          };
          return mapped;
        });
        return {
          tasks: mappedTasks,
          totalCount: response.data.totalCount,
          pageNumber: response.data.pageNumber,
          pageSize: response.data.pageSize,
          totalPages: response.data.totalPages,
          hasNextPage: response.data.hasNextPage,
          hasPreviousPage: response.data.hasPreviousPage
        };
      } catch {
        if (pageParam === 1) {
          const cachedTasks = readTasksCache(token);
          if (cachedTasks?.length) {
            return {
              tasks: cachedTasks,
              totalCount: cachedTasks.length,
              pageNumber: 1,
              pageSize: PAGE_SIZE,
              totalPages: Math.ceil(cachedTasks.length / PAGE_SIZE),
              hasNextPage: cachedTasks.length > PAGE_SIZE,
              hasPreviousPage: false
            };
          }
        }
        return { tasks: [], totalCount: 0, pageNumber: pageParam, pageSize: PAGE_SIZE, totalPages: 0, hasNextPage: false, hasPreviousPage: false };
      }
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: TasksPageData | undefined) => lastPage?.hasNextPage ? lastPage.pageNumber + 1 : undefined,
    initialData: cachedTasks && cachedTasks.length > 0 ? {
      pages: [{ 
        tasks: cachedTasks, 
        totalCount: cachedTasks.length, 
        pageNumber: 1, 
        pageSize: PAGE_SIZE,
        totalPages: Math.ceil(cachedTasks.length / PAGE_SIZE),
        hasNextPage: cachedTasks.length > PAGE_SIZE,
        hasPreviousPage: false
      }],
      pageParams: [1]
    } : undefined,
    staleTime: 2 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    // Фолбэк на случай, если SignalR пропал/не доставил финальные события:
    // пока есть pending-задачи — опрашиваем бэкенд часто, чтобы UI не "зависал" на старом статусе.
    // В обычном режиме (без активных задач) лишние запросы не делаем.
    refetchInterval: (query) => {
      const data = (query.state.data as { pages?: TasksPageData[] } | undefined);
      const tasks = data?.pages?.flatMap(p => p?.tasks || []) || [];
      const hasPending = tasks.some(t => t?.status === 'pending');
      return hasPending ? 2000 : false;
    },
    refetchOnMount: 'always',
    refetchOnReconnect: true,
    enabled: !!token,
    useErrorBoundary: false,
    throwOnError: false,
    retry: (failureCount: number) => failureCount < 2,
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 10000),
    select: (data: { pages: TasksPageData[]; pageParams: number[] }) => {
      if (!data?.pages?.length) {
        return { tasks: [], hasNextPage: false, fetchNextPage: false, totalCount: 0 };
      }
      const allTasks = data.pages.flatMap(p => p?.tasks || []);
      const byId = new Map<string, GenerationTask>();
      for (const t of allTasks) {
        if (!t?.id) continue;
        const existing = byId.get(t.id);
        if (!existing) byId.set(t.id, t);
        else {
          byId.set(t.id, {
            ...existing,
            ...t,
            startedAt: t.startedAt || existing.startedAt,
            completedAt: t.completedAt || existing.completedAt,
            title: t.title || existing.title,
            name: t.name || existing.name,
            progress: typeof t.progress === 'number' ? t.progress : existing.progress
          });
        }
      }
      const uniqueTasks = Array.from(byId.values());
      writeTasksCache(token, uniqueTasks);
      const lastPage = data.pages[data.pages.length - 1];
      return { tasks: uniqueTasks, hasNextPage: lastPage?.hasNextPage || false, fetchNextPage: !!data.pages.length, totalCount: data.pages[0]?.totalCount || 0 };
    }
  });
};

// Хук для получения задач (старый метод, оставлен для совместимости)
export const useTasksLegacy = () => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
  const cachedTasks = readTasksCache(token);

  return useQuery({
    queryKey: TASKS_QUERY_KEY,
    queryFn: async () => {
      const response = await tasksApi.getTasks(1, 100); // Получаем первые 100 задач
      const mappedTasks = response.data.tasks.map((apiTask: any): GenerationTask => {
        let status: 'pending' | 'completed' | 'error' | 'cancelled' = 'pending';
        
        const statusString = String(apiTask.status || '').toLowerCase();
        
        if (statusString === 'successful' || statusString === 'successfull') {
          status = 'completed';
        } else if (statusString === 'cancelled') {
          status = 'cancelled';
        } else if (statusString === 'error' || statusString === 'failed') {
          status = 'error';
        } else if (statusString === 'inprogress') {
          status = 'pending';
        }
        
        return {
          id: apiTask.id,
          type: (apiTask.type || 'unknown').toLowerCase() as any,
          title: apiTask.name || `${apiTask.type || 'Unknown'} Generation`,
          status: status,
          progress: status === 'completed' ? 100 : 0,
          startedAt: apiTask.startTime ? new Date(apiTask.startTime).getTime() : Date.now(),
          completedAt: apiTask.endTime ? new Date(apiTask.endTime).getTime() : undefined,
          canCancel: status === 'pending',
          data: undefined,
          redirected: false,
          name: apiTask.name,
          isViewed: apiTask.isViewed,
          error: apiTask.errorMessage,
          knowledgeDocumentId: apiTask.knowledgeDocumentId
        };
      });
      
      // Сохраняем в кэш
      writeTasksCache(token, mappedTasks);
      
      return mappedTasks;
    },
    initialData: cachedTasks || undefined,
    staleTime: 30 * 1000, // 30 секунд для задач (более частые обновления)
    gcTime: 24 * 60 * 60 * 1000, // 24 часа
    refetchOnWindowFocus: true,
    refetchInterval: 15 * 1000, // Обновляем каждые 15 секунд для активных задач
    refetchOnMount: 'always', // Всегда перезагружаем при монтировании
  });
}; 